// GHIN / handicap foundation for the isolated G-New Features sandbox.
//
// FULLY SELF-CONTAINED: its own copy of the handicap math, ported from
// GolfGamesScorecard's utils/handicap.ts. No live imports, no Firebase, no
// AsyncStorage — pure functions, unit-testable under `node --test`.
//
// AUDIT-CORRECTED ROUNDING (the defect caught on the pbccgolf port):
// USGA Appendix C requires the allowance multiplication on the UNROUNDED
// Course Handicap, rounded once at the end. This module always feeds the
// RAW course handicap (calcCourseHandicapRaw) into calcPlayingHandicap; the
// rounded CH (calcCourseHandicapDisplay) is for DISPLAY ONLY and must never
// enter the allowance step. ggHandicap.test.js pins the divergence case.

export const DEFAULT_STROKE_INDEX = [
  10, 2, 16, 6, 14, 4, 18, 8, 12,
  5, 1, 13, 3, 15, 7, 17, 9, 11,
]

// Allowance %-grid options — mirrors GolfGamesScorecard's ALLOWANCE_OPTIONS
// EXACTLY (100 down to 5; 5% steps at the top and bottom, 10% in the middle;
// no 0). Kept identical so the sandbox grid matches the live app one-for-one.
export const ALLOWANCE_OPTIONS = [100, 95, 90, 85, 80, 75, 70, 60, 50, 40, 35, 30, 25, 20, 15, 10, 5]

// ── Course / playing handicap ────────────────────────────────────────────────

/** UNROUNDED Course Handicap (WHS): idx × slope/113 + (rating − par).
 *  Returns 0 on any invalid input so nothing downstream sees NaN. */
export function calcCourseHandicapRaw({ handicapIndex, slopeRating, courseRating, par }) {
  const idx = Number(handicapIndex)
  const slope = Number(slopeRating)
  const rating = Number(courseRating)
  const p = Number(par)
  if (!Number.isFinite(idx) || !Number.isFinite(slope) || slope <= 0
      || !Number.isFinite(rating) || !Number.isFinite(p)) return 0
  return idx * (slope / 113) + (rating - p)
}

/** Rounded Course Handicap — DISPLAY ONLY. Never feed this into the allowance
 *  step (that is the round-before-allowance bug); use calcCourseHandicapRaw. */
export function calcCourseHandicapDisplay(input) {
  return Math.round(calcCourseHandicapRaw(input))
}

/** Resolve a player's effective allowance: a per-player override wins when set
 *  (any non-null value); null/undefined falls back to the all-players default.
 *  Mirrors the live HandicapReviewTab rule (override ? row.allowance : default). */
export function resolveAllowance(playerAllowance, defaultAllowance) {
  return playerAllowance == null ? Number(defaultAllowance) : Number(playerAllowance)
}

/** Playing Handicap = round(UNROUNDED courseHandicap × allowance ÷ 100).
 *  Allowance ≤ 0 / invalid → returns the CH unchanged (100% = no change). */
export function calcPlayingHandicap(courseHandicapRaw, allowance) {
  const ch = Number(courseHandicapRaw)
  const al = Number(allowance)
  if (!Number.isFinite(ch)) return 0
  if (!Number.isFinite(al) || al <= 0) return ch
  return Math.round(ch * (al / 100))
}

/** One-shot: index + tee + par + allowance → { chRaw, chDisplay, playing }.
 *  The audit-correct pipeline in a single call — `playing` derives from the
 *  RAW ch, `chDisplay` is only for showing on the card. */
export function computePlayingHandicap({ handicapIndex, slopeRating, courseRating, par, allowance = 100 }) {
  const chRaw = calcCourseHandicapRaw({ handicapIndex, slopeRating, courseRating, par })
  return {
    chRaw,
    chDisplay: Math.round(chRaw),
    playing: calcPlayingHandicap(chRaw, allowance),
  }
}

/** Effective handicap for allocation: playingHandicap when present, else
 *  courseHandicap × allowance, else bare courseHandicap. */
export function effectiveHandicap(p) {
  if (Number.isFinite(Number(p?.playingHandicap))) return Number(p.playingHandicap)
  if (Number.isFinite(Number(p?.handicapAllowance))) return calcPlayingHandicap(p.courseHandicap, p.handicapAllowance)
  return Number(p?.courseHandicap) || 0
}

// ── Stroke allocation ────────────────────────────────────────────────────────

/** Strokes a player receives on one hole given a course handicap + stroke
 *  index. CH 1–18 → 1 on SI ≤ CH; CH 19+ → a second on SI ≤ CH−18. Plus
 *  handicaps (CH ≤ 0) get 0 (matches GolfGamesScorecard; plus not modeled). */
export function strokesOnHole(courseHandicap, holeIndex, strokeIndex = DEFAULT_STROKE_INDEX) {
  const ch = Number(courseHandicap)
  if (!Number.isFinite(ch) || ch <= 0) return 0
  if (holeIndex < 0 || holeIndex >= 18) return 0
  const c = Math.floor(ch)
  const si = strokeIndex[holeIndex] || DEFAULT_STROKE_INDEX[holeIndex]
  const base = Math.floor(c / 18)
  const extra = (c % 18) >= si ? 1 : 0
  return base + extra
}

/** Allocation modes (from GolfGamesScorecard's Scats handicap modes):
 *   'full'   — standard USGA allocation on the effective handicap.
 *   'cap1'   — cap the handicap at 18 → never 2 strokes on any hole.
 *   'lowest' — off-the-lowest: subtract the field's lowest effective handicap
 *              so the low player plays scratch; everyone else at the diff. */
export function strokesPerHoleForMode(mode, player, allPlayers, strokeIndex = DEFAULT_STROKE_INDEX) {
  let ch = effectiveHandicap(player)
  if (mode === 'cap1') {
    ch = Math.min(ch, 18)
  } else if (mode === 'lowest') {
    const minCh = Math.min(...(allPlayers || []).map(effectiveHandicap))
    ch = Math.max(0, ch - minCh)
  }
  return Array.from({ length: 18 }, (_, h) => strokesOnHole(ch, h, strokeIndex))
}

/** Convert a gross grid `[player][hole]` (null = unentered) to a net grid by
 *  subtracting each player's per-hole strokes. */
export function netScoreGrid(grossScores, players, strokeIndex = DEFAULT_STROKE_INDEX) {
  return (grossScores || []).map((row, pi) => {
    const eff = players?.[pi] ? effectiveHandicap(players[pi]) : 0
    return (row || []).map((s, h) => (s == null ? null : s - strokesOnHole(eff, h, strokeIndex)))
  })
}
